import React, { useEffect, useState } from 'react';
import { Save, Clock, AlertCircle } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { TimezoneSelect } from '../../components/TimezoneSelect';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function AdminSettingsPage() {
  const [s, setS] = useState(null);

  useEffect(() => { api.getSettings().then(setS); }, []);
  if (!s) return <AdminLayout title="Settings"><div className="text-neutral-400">Loading…</div></AdminLayout>;

  const setBH = (wd, key, value) => {
    const bh = { ...(s.business_hours || {}) };
    bh[String(wd)] = { ...(bh[String(wd)] || {}), [key]: value };
    setS({ ...s, business_hours: bh });
  };

  const save = async () => {
    try {
      const payload = { ...s };
      delete payload.id; delete payload.updated_at;
      await api.updateSettings(payload);
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
  };

  return (
    <AdminLayout title="Settings" subtitle="Company, hours, booking policy and branding"
      actions={<button onClick={save} className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] px-4 py-2.5 rounded-md text-[13px] font-semibold"><Save className="w-4 h-4" /> Save</button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Company">
          <Field label="Company name"><input value={s.company_name || ''} onChange={(e) => setS({ ...s, company_name: e.target.value })} className="input" /></Field>
          <Field label="Contact email"><input value={s.contact_email || ''} onChange={(e) => setS({ ...s, contact_email: e.target.value })} className="input" /></Field>
          <Field label="Default timezone"><TimezoneSelect value={s.timezone || 'America/Toronto'} onChange={(v) => setS({ ...s, timezone: v })} dataTestid="admin-default-timezone" /></Field>
        </Card>

        <Card title="Booking policy">
          <Field label="Booking window (days in advance)"><input type="number" value={s.booking_window_days} onChange={(e) => setS({ ...s, booking_window_days: Number(e.target.value) })} className="input" /></Field>
          <Field label="Minimum notice (hours)"><input type="number" value={s.min_notice_hours} onChange={(e) => setS({ ...s, min_notice_hours: Number(e.target.value) })} className="input" /></Field>
          <Field label="Cancellation cutoff (hours)"><input type="number" value={s.cancellation_cutoff_hours} onChange={(e) => setS({ ...s, cancellation_cutoff_hours: Number(e.target.value) })} className="input" /></Field>
          <Field label="Reminder schedule (hours before, comma-separated)">
            <input value={(s.reminder_hours || []).join(', ')} onChange={(e) => setS({ ...s, reminder_hours: e.target.value.split(',').map((x) => Number(x.trim())).filter((x) => !isNaN(x) && x > 0) })} className="input" />
          </Field>
        </Card>

        <Card title="Business hours" className="lg:col-span-2">
          <div className="space-y-2">
            {DAYS.map((day, wd) => {
              const bh = s.business_hours?.[String(wd)] || { open: '09:00', close: '17:00', enabled: false };
              return (
                <div key={day} className="flex items-center gap-4 py-1.5">
                  <label className="flex items-center gap-2 w-32">
                    <input type="checkbox" checked={!!bh.enabled} onChange={(e) => setBH(wd, 'enabled', e.target.checked)} className="accent-[#f5b300]" />
                    <span className="text-[14px] font-medium">{day}</span>
                  </label>
                  {bh.enabled ? (
                    <>
                      <input type="time" value={bh.open} onChange={(e) => setBH(wd, 'open', e.target.value)} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
                      <span className="text-neutral-400">to</span>
                      <input type="time" value={bh.close} onChange={(e) => setBH(wd, 'close', e.target.value)} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
                    </>
                  ) : <span className="text-[12.5px] text-neutral-400">Closed</span>}
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Branding">
          <Field label="Primary color"><input type="color" value={s.brand_primary || '#f5b300'} onChange={(e) => setS({ ...s, brand_primary: e.target.value })} className="input h-11 w-24 p-1" /></Field>
          <Field label="Secondary color"><input type="color" value={s.brand_secondary || '#4a2e1a'} onChange={(e) => setS({ ...s, brand_secondary: e.target.value })} className="input h-11 w-24 p-1" /></Field>
          <p className="text-[12px] text-neutral-500 flex items-start gap-2 mt-2"><AlertCircle className="w-3.5 h-3.5 text-[#c89200] mt-0.5" /> Site-wide brand colors update on next deploy.</p>
        </Card>
      </div>
      <style>{`.input{width:100%;padding:0.7rem 0.9rem;border-radius:0.4rem;border:1px solid #e7e5e4;font-size:14px;outline:none;}.input:focus{border-color:#f5b300;box-shadow:0 0 0 3px rgba(245,179,0,0.18)}`}</style>
    </AdminLayout>
  );
}

function Card({ title, children, className = '' }) {
  return (
    <div className={`rounded-xl bg-white border border-neutral-100 p-6 space-y-4 ${className}`}>
      <h3 className="text-[17px] font-bold text-[#14110f]">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
